/**
 * The board report (operator order 2026-09-10). One report a year: eight
 * sections filled from the record, a few lines from the Privacy Officer under
 * each, sealed, sent to the board through SignoffHub, written back when
 * signed. Zero engineering words on screen.
 */
import React, { useEffect, useState } from 'react';
import { api, Card, Pill, fmtDay, Tip } from './api';

const API = process.env.REACT_APP_API_URL || '';

const STATUS = {
  draft: { label: 'Being written', cls: 'bg-amber-100 text-amber-800' },
  sealed: { label: 'Sealed, ready for the board', cls: 'bg-sky-100 text-sky-800' },
  sent: { label: 'With the board', cls: 'bg-indigo-100 text-indigo-800' },
  approved: { label: 'Received and reviewed by the board', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Returned by the board', cls: 'bg-red-100 text-red-800' },
};

export default function BoardReport({ onBack, refreshOverview }) {
  const [list, setList] = useState(null);
  const [sections, setSections] = useState([]);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(''); const [err, setErr] = useState(''); const [note, setNote] = useState('');
  const year = new Date().getFullYear();

  const loadList = async () => { const d = await api('/report'); if (d.success) { setList(d.reports || []); setSections(d.sections || []); return d.reports || []; } return []; };
  const open = async (id) => { const d = await api(`/report/${id}`); if (d.success) { setReport(d.report); setSections(d.sections || sections); } };
  useEffect(() => { (async () => { const rows = await loadList(); const current = rows.find((r) => r.report_year === year) || rows[0]; if (current) open(current.id); })(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prepare = async () => {
    setBusy('prepare'); setErr('');
    const d = await api('/report', { method: 'POST', body: { report_year: year } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not prepare the report'); return; }
    setReport(d.report); await loadList(); setNote('The numbers were read from the record just now.');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button type="button" onClick={onBack} className="text-xs font-semibold text-slate-500 hover:text-slate-800 mb-2">← Back to the program</button>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Report to the board</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">Once a year the Privacy Officer reports to the governing body: where member information lives and moves, the vendors who hold it, how members' rights were honored, what was disclosed, what broke and what was done, the risk analysis, and the plan. The numbers come from the record; you write the few lines under each section. The board signs that it received and reviewed the report.</p>
        </div>
        {(!report || report.report_year !== year) && (
          <button type="button" onClick={prepare} disabled={busy === 'prepare'} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold shadow-sm disabled:opacity-50">
            {busy === 'prepare' ? 'Reading the record…' : `Prepare the ${year} report`}
          </button>
        )}
      </div>
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>}
      {note && <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">{note}</div>}

      {list && !list.length && !report && (
        <Card><p className="text-sm text-slate-600 py-4 text-center">No report has been prepared yet. Press the button above; the eight sections fill themselves from the program's record, and you write your statement under each.</p></Card>
      )}

      {report && <ReportEditor report={report} sections={sections} setReport={(r) => { setReport(r); loadList(); refreshOverview?.(); }} setErr={setErr} setNote={setNote} onRefreshNumbers={prepare} />}

      {list && list.length > 1 && (
        <Card title="Earlier reports">
          <ul className="divide-y divide-slate-100">
            {list.filter((r) => r.id !== report?.id).map((r) => (
              <li key={r.id} className="py-2 flex items-center gap-3 text-sm">
                <button type="button" onClick={() => open(r.id)} className="font-semibold text-slate-800 hover:text-orange-700">{r.report_year} report</button>
                <Pill cfg={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Pill>
                <span className="text-xs text-slate-500 ml-auto">{r.approved_at ? `Reviewed by the board ${fmtDay(r.approved_at)}` : r.sealed_at ? `Sealed ${fmtDay(r.sealed_at)}` : `Prepared ${fmtDay(r.captured_at)}`}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ReportEditor({ report, sections, setReport, setErr, setNote, onRefreshNumbers }) {
  const [nar, setNar] = useState(report.narratives || {});
  const [busy, setBusy] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [board, setBoard] = useState([{ display_name: '', email: '' }]);
  const [message, setMessage] = useState('');
  useEffect(() => { setNar(report.narratives || {}); }, [report.id, report.narratives]);
  const draft = report.status === 'draft';
  const st = STATUS[report.status] || { label: report.status, cls: 'bg-slate-100 text-slate-700' };

  const save = async () => {
    setBusy('save'); setErr('');
    const d = await api(`/report/${report.id}`, { method: 'PATCH', body: { narratives: nar } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    setReport({ ...report, ...d.report, signers: report.signers || [] }); setNote('Your statements are saved.');
  };
  const seal = async () => {
    if (!window.confirm('Seal this report? The numbers are read one last time, a record hash is written, and nothing can change after. Next year gets a new report.')) return;
    setBusy('seal'); setErr('');
    const saved = await api(`/report/${report.id}`, { method: 'PATCH', body: { narratives: nar } });
    if (!saved.success) { setBusy(''); setErr(saved.message || 'Could not save'); return; }
    const d = await api(`/report/${report.id}/seal`, { method: 'POST', body: {} });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not seal'); return; }
    setReport({ ...d.report, signers: [] }); setNote('Sealed. Send it to the board when you are ready.');
  };
  const send = async () => {
    const recipients = board.filter((b) => b.email.trim()).map((b) => ({ display_name: b.display_name, email: b.email, role: 'board' }));
    if (!recipients.length) { setErr('Name at least one board member or board designee.'); return; }
    setBusy('send'); setErr('');
    const d = await api(`/report/${report.id}/send-to-board`, { method: 'POST', body: { recipients, message } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not send'); return; }
    setReport(d.report); setSendOpen(false); setNote('Sent. Each person receives a link to read the report and sign that they reviewed it. You sign first.');
  };
  const facts = report.facts || {};

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-lg font-bold text-slate-900">{report.report_year} report</span>
          <Pill cfg={st.cls}>{st.label}</Pill>
          <span className="text-xs text-slate-500">Period {fmtDay(report.period_start)} to {fmtDay(report.period_end)}{report.captured_at ? ` · numbers read ${fmtDay(report.captured_at)}` : ''}{report.sealed_at ? ` · sealed ${fmtDay(report.sealed_at)}${report.sealed_by_name ? ` by ${report.sealed_by_name}` : ''}` : ''}</span>
          <span className="ml-auto flex items-center gap-2">
            {draft && <button type="button" onClick={onRefreshNumbers} className="text-xs font-semibold text-slate-600 hover:text-slate-900">Read the numbers again</button>}
            <a href={`${API}/api/privacy-program/report/${report.id}/print`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-orange-700 hover:underline">Print or save as PDF</a>
          </span>
        </div>
        {report.status === 'approved' && (
          <p className="mt-2 text-sm text-emerald-800">Received and reviewed by the board on {fmtDay(report.approved_at)}{report.approved_by ? `, signed by ${report.approved_by}` : ''}. The next report is due {fmtDay(report.next_due)}.</p>
        )}
        {report.status === 'sent' && (
          <div className="mt-2 text-sm text-slate-700">
            With the board since {fmtDay(report.sent_at)}.{' '}
            {(report.signers || []).map((s) => <span key={s.email} className="inline-block mr-3">{s.display_name || s.email}{s.role === 'board' ? ' (board)' : ''}: {s.decision ? String(s.decision).replace(/_/g, ' ') : 'not yet signed'}</span>)}
          </div>
        )}
        {report.status === 'rejected' && <p className="mt-2 text-sm text-red-800">The board returned this report. Prepare a new one for the year after addressing their comments in SignoffHub.</p>}
      </Card>

      <ol className="space-y-3">
        {sections.map((s) => (
          <li key={s.key} className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{s.n}</span>
              <h3 className="font-semibold text-slate-900">{s.title}</h3>
              <Tip text={s.ask} />
            </div>
            {(facts[s.key] || []).length > 0 && (
              <div className="space-y-1 mb-3">
                {facts[s.key].map((f, i) => <p key={i} className="text-sm text-slate-800">{f}</p>)}
              </div>
            )}
            {draft ? (
              <label className="block">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Your statement{s.key === 'assessment' ? ' (required to seal)' : ' (optional)'}</span>
                <textarea value={nar[s.key] || ''} onChange={(e) => setNar({ ...nar, [s.key]: e.target.value })} rows={s.key === 'assessment' ? 6 : 3} placeholder={s.ask}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800" />
              </label>
            ) : (
              (report.narratives || {})[s.key]
                ? <div className="pl-3 border-l-2 border-slate-300 bg-slate-50 rounded-r-lg py-2 pr-3"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Privacy Officer</div><p className="text-sm text-slate-800 whitespace-pre-wrap">{report.narratives[s.key]}</p></div>
                : <p className="text-xs text-slate-400">No statement on this section.</p>
            )}
          </li>
        ))}
      </ol>

      {draft && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={save} disabled={busy === 'save'} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold disabled:opacity-50">{busy === 'save' ? 'Saving…' : 'Save my statements'}</button>
          <button type="button" onClick={seal} disabled={busy === 'seal'} className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">{busy === 'seal' ? 'Sealing…' : 'Seal the report'}</button>
          <span className="text-xs text-slate-500">Sealing writes the record hash. After that the report cannot change.</span>
        </div>
      )}

      {report.status === 'sealed' && (
        <Card title="Send to the board">
          {!sendOpen ? (
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-slate-600 flex-1">The report goes to the board through SignoffHub. You sign first as the Privacy Officer; each board member or designee then signs that they received and reviewed it. Nothing else is approved by this signature.</p>
              <button type="button" onClick={() => setSendOpen(true)} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold">Send to the board</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Board members or designees who will sign</div>
              {board.map((b, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input value={b.display_name} onChange={(e) => setBoard(board.map((x, j) => (j === i ? { ...x, display_name: e.target.value } : x)))} placeholder="Name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <div className="flex gap-2">
                    <input value={b.email} onChange={(e) => setBoard(board.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} placeholder="Email address" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    {board.length > 1 && <button type="button" onClick={() => setBoard(board.filter((_, j) => j !== i))} className="text-xs text-slate-500">Remove</button>}
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setBoard([...board, { display_name: '', email: '' }])} className="text-xs font-semibold text-slate-600 hover:text-slate-900">+ Add another</button>
              <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">A note to the board (optional)</span>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="For example: for the November meeting." /></label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={send} disabled={busy === 'send'} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold disabled:opacity-50">{busy === 'send' ? 'Sending…' : 'Send now'}</button>
                <button type="button" onClick={() => setSendOpen(false)} className="text-sm text-slate-600">Cancel</button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
